/**
 * AI prompt-preview client.
 * For Media Devoted | Created By Ciro Autiero
 */

import api from '@/services/api';
import type {
  FlowLayer,
  FlowOptimizerLibraryPromptPreview,
  FlowOptimizerLibraryPromptTarget,
  FlowOptimizerLibraryProvider,
} from '@trafficpulse/shared';

export interface PromptPreviewQuery {
  provider: FlowOptimizerLibraryProvider;
  campaignId: string;
  /** One or more stat windows — the AI is told to use exactly these as the
   *  generated groups' dateRange values. */
  dateRanges: string[];
  from?: string;
  to?: string;
  layers?: FlowLayer[];
  target: FlowOptimizerLibraryPromptTarget;
  objective?: string;
  /** Ceilings for the ask — the AI may return fewer, never more. */
  maxFormulas?: number;
  maxRules?: number;
  /** Client's rule behavior: swap losers out, or adjust in place only. */
  ruleStyle?: 'rotate' | 'in-place';
}

export async function fetchPromptPreview(
  query: PromptPreviewQuery,
): Promise<FlowOptimizerLibraryPromptPreview> {
  const params = new URLSearchParams({
    provider: query.provider,
    campaignId: query.campaignId,
    dateRange: query.dateRanges[0] ?? 'last-7-days',
    dateRanges: query.dateRanges.join(','),
    target: query.target,
  });
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.layers?.length) params.set('layers', query.layers.join(','));
  if (query.objective?.trim()) params.set('objective', query.objective.trim());
  if (query.maxFormulas) params.set('maxFormulas', String(query.maxFormulas));
  if (query.maxRules) params.set('maxRules', String(query.maxRules));
  if (query.ruleStyle) params.set('ruleStyle', query.ruleStyle);
  const { data } = await api.get(
    `/flow-optimizer-library/prompt-preview?${params.toString()}`,
  );
  return (data.data ?? data) as FlowOptimizerLibraryPromptPreview;
}

export interface TeamPromptContext {
  content: string;
  updatedAt: string | null;
}

export async function fetchTeamContext(): Promise<TeamPromptContext> {
  const { data } = await api.get('/flow-optimizer-library/prompt-context');
  return (data.data ?? data) as TeamPromptContext;
}

export async function saveTeamContext(
  content: string,
): Promise<TeamPromptContext> {
  const { data } = await api.put('/flow-optimizer-library/prompt-context', {
    content,
  });
  return (data.data ?? data) as TeamPromptContext;
}
